import React from 'react';
import { Bell, Wifi, WifiOff, Sun, Moon, LogOut, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAppStore } from '@/stores/appStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { cn } from '@/utils';

export function Header() {
  const { user, logout } = useAuth();
  const { wsConnected, theme, toggleTheme, unreadCount, markAllRead, notifications } = useAppStore();
  const [showNotifications, setShowNotifications] = React.useState(false);

  return (
    <header className="h-16 border-b border-surface-800 bg-surface-900/50 backdrop-blur-sm flex items-center justify-between px-6">
      {/* Left: Breadcrumb area */}
      <div className="flex items-center gap-4">
        <h1 className="text-sm text-surface-400">
          Mr Ai Coin
        </h1>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* WebSocket Status */}
        <div className={cn('flex items-center gap-1.5 text-xs', wsConnected ? 'text-profit' : 'text-loss')}>
          {wsConnected ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Offline'}</span>
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors"
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 rounded-lg text-surface-500 hover:text-surface-300 hover:bg-surface-800 transition-colors relative"
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-loss text-white text-[10px] rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-surface-900 border border-surface-800 rounded-xl shadow-2xl z-50">
              <div className="flex items-center justify-between p-3 border-b border-surface-800">
                <span className="text-sm font-medium text-surface-200">Notifications</span>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-brand-400 hover:text-brand-300"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-xs text-surface-500">
                    No notifications
                  </div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <div
                      key={n.id}
                      className={cn(
                        'p-3 border-b border-surface-800/50 hover:bg-surface-800/30',
                        !n.is_read && 'bg-brand-500/5',
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-surface-200">{n.title}</span>
                        <span className="text-[10px] text-surface-600">{n.type}</span>
                      </div>
                      <p className="text-xs text-surface-400 mt-0.5 line-clamp-2">{n.message}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* User */}
        <div className="flex items-center gap-2 pl-2 border-l border-surface-800">
          <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center">
            <User className="w-4 h-4 text-brand-400" />
          </div>
          <div className="hidden sm:block">
            <p className="text-xs font-medium text-surface-200">{user?.username || 'User'}</p>
            <p className="text-[10px] text-surface-500">{user?.is_admin ? 'Admin' : 'Trader'}</p>
          </div>
          <button
            onClick={logout}
            className="p-2 rounded-lg text-surface-500 hover:text-loss hover:bg-surface-800 transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
